import { useEffect, useState } from "react";
import { api, apiError, eur } from "@/lib/api";
import { useI18n } from "@/i18n";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Th, Td } from "@/components/common";
import { toast } from "sonner";
import { Info } from "lucide-react";

const INHERIT = "__inherit__";

/** Preisführung je Preisliste + Steuer-Vorschau (reine Vorschau, keine Massenmutation). */
export const PriceAuthorityPanel = ({ priceList, canEdit, onSaved }) => {
  const { t, fmtDate } = useI18n();
  const [preview, setPreview] = useState(null);
  const [code, setCode] = useState("STANDARD");
  const value = priceList?.price_authority || INHERIT;
  const effective = priceList?.price_authority_effective;

  useEffect(() => {
    if (!priceList?.id) { setPreview(null); return; }
    api.get(`/price-lists/${priceList.id}/tax-preview`, { params: { code } })
      .then((r) => setPreview(r.data)).catch(() => setPreview(null));
  }, [priceList?.id, priceList?.price_authority, code]);

  const save = async (v) => {
    try {
      await api.put(`/price-lists/${priceList.id}`, { price_authority: v === INHERIT ? null : v });
      toast.success(t("common.saved"));
      onSaved?.();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="rounded-md border border-border p-3 mb-4 space-y-2" data-testid="pl-authority-panel">
      <Label>{t("tax.priceAuthority")}</Label>
      <Select value={value} onValueChange={save} disabled={!canEdit}>
        <SelectTrigger className="mt-1" data-testid="pl-price-authority"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value={INHERIT}>{t("taxc.authorityFromTenant")}</SelectItem>
          <SelectItem value="NET_AUTHORITATIVE">{t("taxc.netAuthoritative")}</SelectItem>
          <SelectItem value="GROSS_AUTHORITATIVE">{t("taxc.grossAuthoritative")}</SelectItem>
        </SelectContent>
      </Select>
      <p className="text-xs text-muted-foreground" data-testid="pl-authority-effective">
        {effective === "GROSS_AUTHORITATIVE" ? t("tax.priceAuthorityGross") : t("tax.priceAuthorityNet")}
        {priceList?.price_authority_source === "price_list" ? ` · ${t("taxc.authorityOwn")}` : ` · ${t("taxc.authorityFromTenant")}`}
      </p>

      {preview && (
        <div className="pt-2 border-t border-border space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            <Label className="text-xs">{t("taxc.previewTitle")}</Label>
            <Select value={code} onValueChange={setCode}>
              <SelectTrigger className="h-8 w-40" data-testid="pl-preview-code"><SelectValue /></SelectTrigger>
              <SelectContent>{["STANDARD", "REDUCED", "ZERO_RATE"].map((c) => <SelectItem key={c} value={c}>{t(`tax.code.${c}`, c)}</SelectItem>)}</SelectContent>
            </Select>
            <span className="text-xs text-muted-foreground font-mono" data-testid="pl-preview-rates">
              {preview.old_rate} % → {preview.new_rate} % · {fmtDate(preview.valid_from)}
            </span>
          </div>
          {preview.rows.length === 0
            ? <p className="text-xs text-muted-foreground">{t("pl.noPrices", "—")}</p>
            : (
              <div className="max-h-48 overflow-auto rounded-md border border-border">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50"><tr>
                    <Th right>{t("taxc.netBefore")}</Th><Th right>{t("taxc.grossBefore")}</Th>
                    <Th right>{t("taxc.netAfter")}</Th><Th right>{t("taxc.grossAfter")}</Th>
                  </tr></thead>
                  <tbody data-testid="pl-preview-rows">
                    {preview.rows.slice(0, 50).map((r, i) => (
                      <tr key={i}>
                        <Td right mono>{eur(r.net_before)}</Td><Td right mono>{eur(r.gross_before)}</Td>
                        <Td right mono>{eur(r.net_after)}</Td><Td right mono>{eur(r.gross_after)}</Td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          <p className="flex gap-1.5 text-xs text-muted-foreground"><Info className="h-3.5 w-3.5 shrink-0" /> {t("taxc.previewHint")}</p>
        </div>
      )}
    </div>
  );
};
